type CommissionGroup = {
  commissionInfo?: Array<unknown> | null;
};

/**
 * Invoice detail and invoice list rows do not always carry the same shape.
 * Billing detail hides Update Invoice when a commission payment record exists
 * (`commissionInfo`) or the invoice came from a NetSuite webhook. List rows
 * expose the same recorded payments as paid-amount fields when the nested
 * groups are absent.
 */
export type UpdateInvoiceCandidate = {
  invoiceStatus?: string | null;
  fromNetsuiteWebhook?: boolean | null;
  agencyCommissions?: CommissionGroup[] | null;
  ovrAgencyCommissions?: CommissionGroup[] | null;
  v3Commissions?: CommissionGroup | null;
  v3CommissionPaidAmount?: number | string | null;
  agencyCommissionPaidAmount?: number | string | null;
  ovrAgencyCommissionPaidAmount?: number | string | null;
  totalV3Paid?: number | string | null;
  totalAgencyPaid?: number | string | null;
  totalOvrPaid?: number | string | null;
};

const toAmount = (value: number | string | null | undefined): number => {
  if (value == null || value === "") return 0;
  const amount = typeof value === "number" ? value : Number(String(value).replace(/[$,]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const hasCommissionRecords = (group: CommissionGroup | null | undefined): boolean =>
  (group?.commissionInfo?.length ?? 0) > 0;

const hasRecordedCommission = (invoice: UpdateInvoiceCandidate): boolean => {
  const hasCommissionGroups =
    invoice.agencyCommissions != null ||
    invoice.ovrAgencyCommissions != null ||
    invoice.v3Commissions != null;

  if (hasCommissionGroups) {
    return (
      !!invoice.agencyCommissions?.some(hasCommissionRecords) ||
      !!invoice.ovrAgencyCommissions?.some(hasCommissionRecords) ||
      hasCommissionRecords(invoice.v3Commissions)
    );
  }

  return (
    toAmount(invoice.v3CommissionPaidAmount) > 0 ||
    toAmount(invoice.agencyCommissionPaidAmount) > 0 ||
    toAmount(invoice.ovrAgencyCommissionPaidAmount) > 0 ||
    toAmount(invoice.totalV3Paid) > 0 ||
    toAmount(invoice.totalAgencyPaid) > 0 ||
    toAmount(invoice.totalOvrPaid) > 0
  );
};

/** Same visibility rule as the Update Invoice button on billing detail. */
export const canShowUpdateInvoice = (
  invoice: UpdateInvoiceCandidate | null | undefined,
  canAccessEditInvoice: boolean
): boolean => {
  if (!invoice || !canAccessEditInvoice) return false;
  if (invoice.invoiceStatus === "CLOSED") return false;
  if (invoice.fromNetsuiteWebhook) return false;
  return !hasRecordedCommission(invoice);
};
